import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Eye, EyeOff, Award, Star, Gift, CheckCircle, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "@/App";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import CreditsEarnedAnimation from "@/components/CreditsEarnedAnimation";

// Backend stores birthdays as a full "YYYY-MM-DD" string (validated against
// `%Y-%m-%d`). The birthday-rewards cron only matches the trailing `-MM-DD`
// — the year is ignored. Customers find entering their birth year off-putting
// and most ask why we'd need it, so the UI collects only month + day and
// stamps a stable placeholder year on submit. Choosing 1900 (not a leap
// year) so Feb 29 selections are never created.
const BIRTHDAY_PLACEHOLDER_YEAR = "1900";

const BIRTHDAY_MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

// Days per month for the placeholder year (1900 — non-leap, so Feb has 28).
const DAYS_IN_MONTH = {
  "01": 31, "02": 28, "03": 31, "04": 30, "05": 31, "06": 30,
  "07": 31, "08": 31, "09": 30, "10": 31, "11": 30, "12": 31,
};

const AuthModal = ({ open, onClose, onLogin, initialMode = "login" }) => {
  const [isLogin, setIsLogin] = useState(initialMode === "login");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code, 3: new password
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [referralCodeValid, setReferralCodeValid] = useState(null);
  const [showCreditsAnimation, setShowCreditsAnimation] = useState(false);
  const [creditsEarnedData, setCreditsEarnedData] = useState({ type: "rewards", amount: 0, message: "" });
  const [rewardsLinkCopied, setRewardsLinkCopied] = useState(false);
  const REWARDS_PUBLIC_URL = "https://octasker.com/rewards";

  const copyRewardsLink = async () => {
    try {
      await navigator.clipboard.writeText(REWARDS_PUBLIC_URL);
      setRewardsLinkCopied(true);
      toast.success("Shareable link copied!");
      setTimeout(() => setRewardsLinkCopied(false), 2500);
    } catch (e) {
      toast.error("Could not copy — long-press the link to copy manually.");
    }
  };

  const shareRewardsLink = async () => {
    const data = {
      title: "Done Right Rewards by OC Tasker",
      text: "Earn 5–10% cashback on every handyman service. Free to join, no annual fee.",
      url: REWARDS_PUBLIC_URL,
    };
    if (navigator.share) {
      try { await navigator.share(data); } catch (e) { /* user cancelled */ }
    } else {
      copyRewardsLink();
    }
  };
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
    address: "",
    referral_code_used: "",
    sms_consent: false,
    how_heard: "",
    date_of_birth: ""
  });

  // Update isLogin when initialMode changes or modal opens
  useEffect(() => {
    if (open) {
      setIsLogin(initialMode === "login");
    }
  }, [initialMode, open]);

  // Check for referral code in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referral_code_used: refCode }));
      validateReferralCode(refCode);
    }
    
    // Pre-fill address from cart if available
    const savedCart = localStorage.getItem('octasker_cart');
    if (savedCart) {
      try {
        const { items } = JSON.parse(savedCart);
        if (items && items.length > 0 && items[0].address) {
          setFormData(prev => ({ ...prev, address: items[0].address }));
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
  }, []);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralCodeValid(null);
      return;
    }
    try {
      const response = await axiosInstance.get(`/referrals/validate/${code}`);
      setReferralCodeValid(response.data);
    } catch {
      setReferralCodeValid({ valid: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate SMS consent for registration
    if (!isLogin && !formData.sms_consent) {
      toast.error("Please agree to receive SMS messages to continue");
      return;
    }
    
    setLoading(true);

    const endpoint = isLogin ? "/auth/login" : "/auth/register";
    // Sanitise partial DOB selections — backend validator requires a complete
    // "YYYY-MM-DD". If the customer picked only month OR only day, drop the
    // half-set value rather than rejecting their whole registration.
    let cleanedFormData = formData;
    if (!isLogin) {
      const dob = (formData.date_of_birth || "").trim();
      if (dob && (dob.endsWith("-00") || dob.includes("-00-"))) {
        cleanedFormData = { ...formData, date_of_birth: "" };
      }
    }
    const submitData = isLogin 
      ? { email: formData.email, password: formData.password }
      : cleanedFormData;

    // Auto-retry once on transient network errors. The customer-facing AuthModal
    // is often hit right after a long-running admin task (e.g. Smart Estimator
    // auto-fix, 60-180s) that can saturate the Cloudflare connection pool —
    // a fresh retry almost always succeeds.
    let response;
    try {
      try {
        response = await axiosInstance.post(endpoint, submitData);
      } catch (firstErr) {
        const isNetworkError = !firstErr.response;
        if (!isNetworkError) throw firstErr;
        // Wait 1.2s and retry once
        await new Promise((r) => setTimeout(r, 1200));
        response = await axiosInstance.post(endpoint, submitData);
      }
      
      // Show referral bonus message if applied
      if (response.data.referral_bonus_applied) {
        setCreditsEarnedData({
          type: "referral",
          amount: response.data.bonus_points,
          message: "Welcome bonus for using a referral code!"
        });
        setShowCreditsAnimation(true);
      } else if (response.data.signup_credit) {
        // Show signup credit animation for new users
        setCreditsEarnedData({
          type: "service",
          amount: response.data.signup_credit,
          message: "Welcome to OC Tasker! Use towards your first service."
        });
        setShowCreditsAnimation(true);
      }
      
      onLogin(response.data.user, response.data.token, response.data);
      setFormData({
        email: "",
        password: "",
        full_name: "",
        phone: "",
        address: "",
        referral_code_used: "",
        sms_consent: false,
        how_heard: "",
        date_of_birth: ""
      });
      setReferralCodeValid(null);
    } catch (error) {
      // Distinguish network errors (no response from server) from credential
      // rejections (401 with detail). Network errors are usually transient
      // Cloudflare/connection hiccups — tell the user that explicitly so they
      // don't think it's a bad password.
      if (!error.response) {
        toast.error("Network hiccup — your sign-in didn't reach the server. Please try again. If this keeps happening, try clearing cookies or using an incognito window.");
      } else {
        toast.error(error.response?.data?.detail || "Authentication failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.formatted_address || addressData.street_address
    }));
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (resetStep === 1) {
        // Send reset code
        const res = await axiosInstance.post("/auth/forgot-password", { email: resetEmail });
        const masked = res.data?.sent_to_masked;
        // Keep response ambiguous for security (we don't confirm whether the
        // account exists) but still give a strong "check spam" nudge — many
        // mail servers quarantine self-domain mail.
        toast.success(
          masked
            ? `Reset code sent to ${masked}`
            : "If an account exists with that email, a reset code has been sent.",
          {
            description: "Check your inbox AND your spam / junk folder — sometimes self-domain emails get filtered. Code expires in 15 minutes.",
            duration: 8000,
          }
        );
        setResetStep(2);
      } else if (resetStep === 2) {
        // Verify code
        await axiosInstance.post("/auth/verify-reset-code", { email: resetEmail, code: resetCode });
        toast.success("Code verified!");
        setResetStep(3);
      } else if (resetStep === 3) {
        // Reset password
        await axiosInstance.post("/auth/reset-password", {
          email: resetEmail,
          code: resetCode,
          new_password: newPassword
        });
        toast.success("Password reset successful! You can now login.");
        setShowForgotPassword(false);
        setResetStep(1);
        setResetEmail("");
        setResetCode("");
        setNewPassword("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordFlow = () => {
    setShowForgotPassword(false);
    setResetStep(1);
    setResetEmail("");
    setResetCode("");
    setNewPassword("");
  };

  // Handle dialog close - prevent closing when clicking on Google Places dropdown
  const handleDialogClose = (open) => {
    // Check if the click was on a Google Places dropdown element
    const pacContainer = document.querySelector('.pac-container');
    if (pacContainer && pacContainer.matches(':hover')) {
      // Don't close the dialog if user is interacting with Google Places
      return;
    }
    onClose(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent 
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto" 
        data-testid="auth-modal"
        onPointerDownOutside={(e) => {
          // Prevent closing when clicking on Google Places autocomplete dropdown
          const target = e.target;
          if (target.closest('.pac-container') || target.classList.contains('pac-item')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          // Also prevent on interact outside
          const target = e.target;
          if (target.closest('.pac-container') || target.classList.contains('pac-item')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-black">
            {showForgotPassword 
              ? "Reset Password" 
              : (isLogin ? "Welcome Back" : "Join Done Right Rewards")}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {showForgotPassword 
              ? "We'll send a password reset code to your email" 
              : (isLogin ? "Sign in to book services and earn rewards" : "Create your free account to start saving")}
          </DialogDescription>
        </DialogHeader>

        {/* Rewards Program Pitch - Registration only */}
        {!isLogin && !showForgotPassword && (
          <>
            {/* Shareable public-link strip — admins (and curious customers) can grab
                the canonical Rewards URL right from the popup. This was added because
                the in-app `/loyalty` URL is auth-gated; the `/rewards` page is the
                public landing page that always works when shared via SMS/iMessage. */}
            <div
              className="bg-slate-900 text-white rounded-xl p-3 mt-2 mb-1"
              data-testid="rewards-modal-share-card"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                  Shareable Rewards link
                </span>
                <a
                  href="/rewards"
                  className="text-[11px] text-orange-300 hover:text-orange-200 underline flex items-center gap-1"
                  data-testid="rewards-modal-open-link"
                >
                  Open page <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 bg-white/95 text-gray-900 text-xs sm:text-sm px-2.5 py-2 rounded-md font-mono truncate select-all"
                  data-testid="rewards-modal-url"
                  title={REWARDS_PUBLIC_URL}
                >
                  {REWARDS_PUBLIC_URL}
                </code>
                <Button
                  type="button"
                  size="sm"
                  onClick={copyRewardsLink}
                  data-testid="rewards-modal-copy"
                  className="bg-amber-400 text-slate-900 hover:bg-amber-300 font-semibold shrink-0 h-9"
                >
                  {rewardsLinkCopied ? (
                    <><Check className="w-4 h-4 mr-1" />Copied</>
                  ) : (
                    <><Copy className="w-4 h-4 mr-1" />Copy</>
                  )}
                </Button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={shareRewardsLink}
                    data-testid="rewards-modal-share"
                    variant="outline"
                    className="bg-transparent border-amber-400 text-amber-200 hover:bg-amber-400/10 font-semibold shrink-0 h-9"
                  >
                    Share
                  </Button>
                )}
              </div>
            </div>

          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 mt-2 mb-1">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                <Award className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-1">Done Right Rewards · Earn Cashback on Every Service</h4>
                <p className="text-xs text-gray-600 mb-2">
                  Free to join — auto-enrolled with your first booking. Earn 5–10% cashback on every job, applied automatically to your next service.
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <span className="flex items-center gap-1.5 text-xs text-gray-700">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" /> $25 signup credit
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-700">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" /> Up to 10% cashback
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-700">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" /> $25 birthday bonus
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-gray-700">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" /> Refer friends, earn 10% of their first job
                  </span>
                </div>
              </div>
            </div>
          </div>
          </>
        )}

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            {resetStep === 1 && (
              <div className="space-y-2">
                <Label htmlFor="reset_email" className="text-black">Email Address</Label>
                <Input
                  id="reset_email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  data-testid="reset-email"
                  className="text-black"
                />
              </div>
            )}

            {resetStep === 2 && (
              <div className="space-y-2">
                <Label htmlFor="reset_code" className="text-black">Enter 6-Digit Code</Label>
                <Input
                  id="reset_code"
                  type="text"
                  maxLength={6}
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="000000"
                  required
                  data-testid="reset-code"
                  className="text-black text-center text-2xl tracking-widest"
                />
                <p className="text-xs text-gray-600">Check your email for the 6-digit code we sent to {resetEmail}</p>
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 mt-1 text-amber-800" data-testid="reset-spam-hint">
                  <strong>Don't see it?</strong> Check your spam / junk folder —
                  emails from <code>Info@octasker.com</code> to another <code>@octasker.com</code>
                  address sometimes get filtered. The code expires in 15 minutes.
                </div>
              </div>
            )}

            {resetStep === 3 && (
              <div className="space-y-2">
                <Label htmlFor="new_password" className="text-black">New Password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid="new-password"
                    className="text-black pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? "Please wait..." : (resetStep === 1 ? "Send Code" : resetStep === 2 ? "Verify Code" : "Reset Password")}
            </Button>

            <button
              type="button"
              onClick={() => {
                resetPasswordFlow();
                setShowForgotPassword(false);
              }}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium w-full text-center"
            >
              Back to Login
            </button>
          </form>
        ) : (
          <>
            <form 
              onSubmit={handleSubmit} 
              className="space-y-4 mt-4"
              onKeyDown={(e) => {
                // Prevent form submission via Enter when focus is on address field
                if (e.key === 'Enter' && e.target.getAttribute('data-testid') === 'auth-address') {
                  e.preventDefault();
                  e.stopPropagation();
                  return false;
                }
              }}
            >
          <div className="space-y-2">
            <Label htmlFor="email" className="text-black">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              data-testid="auth-email"
              className="text-black"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-black">Password</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="auth-password"
                className="text-black pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {!isLogin && (
            <>
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-black">Full Name</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  data-testid="auth-fullname"
                  className="text-black"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-black">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  data-testid="auth-phone"
                  className="text-black"
                />
              </div>
              
              {/* SMS Consent - right after phone number field */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <Checkbox
                  id="sms_consent"
                  checked={formData.sms_consent}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sms_consent: checked }))}
                  className="mt-1"
                  data-testid="auth-sms-consent"
                />
                <Label htmlFor="sms_consent" className="text-sm text-gray-700 font-normal leading-relaxed cursor-pointer">
                  I agree to receive text updates about my estimate and appointment scheduling. Reply STOP to opt out. View our{" "}
                  <a href="/terms" target="_blank" className="text-orange-600 hover:underline">Terms</a>
                  {" "}and{" "}
                  <a href="/privacy" target="_blank" className="text-orange-600 hover:underline">Privacy Policy</a>
                  {" "}for full details.
                </Label>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address" className="text-black">Address</Label>
                <AddressAutocomplete
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  onPlaceSelect={handleAddressSelect}
                  placeholder="Start typing your address..."
                  data-testid="auth-address"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="date_of_birth_month" className="text-black flex items-center gap-2">
                  <Gift className="w-4 h-4 text-orange-500" />
                  Birthday <span className="text-gray-500 font-normal">(Optional — get $25 cashback every year!)</span>
                </Label>
                {(() => {
                  // Parse the stored "YYYY-MM-DD" back into MM / DD for the dropdowns.
                  // Empty string → no selection.
                  const dob = formData.date_of_birth || "";
                  const parts = dob.split("-");
                  const currentMonth = parts.length === 3 ? parts[1] : "";
                  const currentDay = parts.length === 3 ? parts[2] : "";
                  const maxDay = currentMonth ? DAYS_IN_MONTH[currentMonth] || 31 : 31;

                  const updateDob = (month, day) => {
                    if (!month && !day) {
                      setFormData((prev) => ({ ...prev, date_of_birth: "" }));
                      return;
                    }
                    // Snap day back if month change made it invalid
                    // (e.g. Feb 30 → Feb 28).
                    let safeDay = day;
                    if (month && day) {
                      const dim = DAYS_IN_MONTH[month] || 31;
                      if (parseInt(day, 10) > dim) safeDay = String(dim).padStart(2, "0");
                    }
                    // We only commit a full DOB once BOTH parts are chosen.
                    if (month && safeDay) {
                      setFormData((prev) => ({
                        ...prev,
                        date_of_birth: `${BIRTHDAY_PLACEHOLDER_YEAR}-${month}-${safeDay}`,
                      }));
                    } else {
                      // Partial selection — temporarily store with a "00" placeholder for
                      // the missing half so React sees the update; we strip incomplete
                      // values on submit.
                      setFormData((prev) => ({
                        ...prev,
                        date_of_birth: month && !safeDay
                          ? `${BIRTHDAY_PLACEHOLDER_YEAR}-${month}-00`
                          : !month && safeDay
                          ? `${BIRTHDAY_PLACEHOLDER_YEAR}-00-${safeDay}`
                          : "",
                      }));
                    }
                  };

                  return (
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        value={currentMonth || undefined}
                        onValueChange={(m) => updateDob(m, currentDay)}
                      >
                        <SelectTrigger
                          id="date_of_birth_month"
                          data-testid="auth-dob-month"
                          className="text-black"
                        >
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {BIRTHDAY_MONTHS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={currentDay && currentDay !== "00" ? currentDay : undefined}
                        onValueChange={(d) => updateDob(currentMonth, d)}
                      >
                        <SelectTrigger
                          id="date_of_birth_day"
                          data-testid="auth-dob-day"
                          className="text-black"
                        >
                          <SelectValue placeholder="Day" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: maxDay }, (_, i) => {
                            const dd = String(i + 1).padStart(2, "0");
                            return (
                              <SelectItem key={dd} value={dd}>
                                {parseInt(dd, 10)}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
                <p className="text-xs text-gray-500">
                  We'll auto-credit you $25 in Done Right Rewards cashback every year on your birthday. We only need the month and day — no year required.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="referral_code" className="text-black">Referral Code (Optional)</Label>
                <Input
                  id="referral_code"
                  name="referral_code_used"
                  type="text"
                  value={formData.referral_code_used}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase();
                    setFormData(prev => ({ ...prev, referral_code_used: code }));
                    if (code.length >= 4) {
                      validateReferralCode(code);
                    } else {
                      setReferralCodeValid(null);
                    }
                  }}
                  placeholder="Enter referral code"
                  data-testid="auth-referral-code"
                  className={`text-black uppercase ${
                    referralCodeValid?.valid === true ? 'border-green-500 bg-green-50' :
                    referralCodeValid?.valid === false ? 'border-red-500 bg-red-50' : ''
                  }`}
                />
                {referralCodeValid?.valid === true && (
                  <p className="text-xs text-green-600">
                    Referred by {referralCodeValid.referrer_name} - You'll get ${referralCodeValid.signup_bonus_value} in bonus points!
                  </p>
                )}
                {referralCodeValid?.valid === false && (
                  <p className="text-xs text-red-600">Invalid referral code</p>
                )}
              </div>
              
              {/* How did you hear about us? */}
              <div className="space-y-2">
                <Label className="text-black">How did you hear about us?</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["Google Search", "Referral", "Nextdoor", "Facebook", "Instagram", "Yelp", "Returning Customer", "Other"].map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm transition-all ${
                        formData.how_heard === option 
                          ? "border-orange-500 bg-orange-50 text-black" 
                          : "border-gray-200 hover:border-orange-200 text-gray-700"
                      }`}
                    >
                      <input
                        type="radio"
                        name="how_heard"
                        value={option}
                        checked={formData.how_heard === option}
                        onChange={(e) => setFormData(prev => ({ ...prev, how_heard: e.target.value }))}
                        className="accent-orange-500"
                        data-testid={`auth-how-heard-${option.toLowerCase().replace(/\s+/g, '-')}`}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
          <Button 
            type="submit" 
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            disabled={loading}
            data-testid="auth-submit"
          >
            {loading ? "Please wait..." : (isLogin ? "Sign In" : "Create Account")}
          </Button>
        </form>
        <div className="text-center mt-4 space-y-2">
          {isLogin && (
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium block w-full"
              data-testid="forgot-password-link"
            >
              Forgot Password?
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            data-testid="auth-toggle"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
          <p className="text-xs text-gray-600 mt-4">
            By signing in you agree to our{" "}
            <a href="/terms" className="text-orange-600 hover:text-orange-700 underline">
              terms of use
            </a>{" "}
            and{" "}
            <a href="/privacy" className="text-orange-600 hover:text-orange-700 underline">
              privacy policy
            </a>
            .
          </p>
        </div>
          </>
        )}
      </DialogContent>
      
      {/* Credits Earned Animation */}
      <CreditsEarnedAnimation
        show={showCreditsAnimation}
        type={creditsEarnedData.type}
        amount={creditsEarnedData.amount}
        message={creditsEarnedData.message}
        onComplete={() => setShowCreditsAnimation(false)}
      />
    </Dialog>
  );
};

export default AuthModal;
